import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMRIMachineById } from '../../services/apiService';
import { getDownloadUrl } from '../../services/api/fileService';
import LoadingRing from '../ui-components/LoadingRing';
import MRIMachineForm from './MRIMachineForm';
import MachineDetailView from './MachineDetailView';
import { MRIMachinePopulated, MRIMachineFormData } from './types';
import { useToast } from '../../hooks/use-toast';

interface MRIMachineProps {
    machineId: string;
    onClose?: () => void;
    onChange?: () => void;
    canEdit?: boolean;
}

export default function MRIMachine({
    machineId,
    onClose,
    onChange,
    canEdit = false,
}: MRIMachineProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [machine, setMachine] = useState<MRIMachinePopulated | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [machineImageUrl, setMachineImageUrl] = useState<string | null>(null);
    const [loadError, setLoadError] = useState(false);

    const getData = useCallback(async () => {
        setIsLoading(true);
        setLoadError(false);
        try {
            const machineData = await getMRIMachineById(machineId);

            if (!machineData) {
                setLoadError(true);
                toast({
                    title: t('roomOverview.mriMachine.loadError'),
                    description: t(
                        'roomOverview.mriMachine.loadErrorDescription'
                    ),
                    variant: 'destructive',
                });
                setIsLoading(false);
                return;
            }

            setMachine(machineData);

            // Fetch signed URL for machine image
            if (machineData.machineImage?._id) {
                try {
                    const url = await getDownloadUrl(
                        machineData.machineImage._id
                    );
                    setMachineImageUrl(url || null);
                } catch (error) {
                    console.error('Failed to fetch image URL:', error);
                }
            }
        } catch (error) {
            console.error('Failed to load machine:', error);
            setLoadError(true);
            toast({
                title: t('roomOverview.mriMachine.loadError'),
                description: t('roomOverview.mriMachine.loadErrorDescription'),
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [machineId, t, toast]);

    useEffect(() => {
        getData();
    }, [getData]);

    const handleClose = () => {
        onClose?.();
    };

    const formData: MRIMachineFormData | null = useMemo(() => {
        if (!machine) return null;
        return {
            ...machine,
            attachedFiles: machine.attachedFiles ?? [],
            machineImage: machine.machineImage?._id || '',
            machineImageUrl: machineImageUrl || '',
            notes: machine.notes || '',
        };
    }, [machine, machineImageUrl]);

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (isEditing && formData) {
        return (
            <MRIMachineForm
                MRImachine={formData}
                isNew={false}
                actions="panel"
                onCancel={() => setIsEditing(false)}
                onClose={handleClose}
                onChange={onChange}
            />
        );
    }

    if (!machine || loadError) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-gray-400">
                    {t('roomOverview.mriMachine.notFound')}
                </p>
            </div>
        );
    }

    return (
        <MachineDetailView
            machine={machine}
            onEdit={canEdit ? () => setIsEditing(true) : undefined}
            onClose={handleClose}
            onChange={onChange}
            readOnly={!canEdit}
            stickyHeader
        />
    );
}
